import { IStatusPill } from 'types/ui/statusPill.interface'
import { IOpsRequest, OpsRequestPriority, OpsRequestSlaState, OpsRequestStatus } from 'types/opsRequests.interface'

export const REQUEST_STATUS_META: { [k in OpsRequestStatus]: { label: string; tone: IStatusPill['status'] } } = {
  open: { label: 'Open', tone: 'info' },
  acknowledged: { label: 'Acknowledged', tone: 'accent' },
  'in_progress': { label: 'In progress', tone: 'success' },
  done: { label: 'Done', tone: 'success' },
  declined: { label: 'Declined', tone: 'neutral' },
}

export const REQUEST_PRIORITY_META: { [k in OpsRequestPriority]: { label: string; tone: IStatusPill['status'] } } = {
  low: { label: 'Low', tone: 'neutral' },
  normal: { label: 'Normal', tone: 'info' },
  high: { label: 'High', tone: 'warning' },
  critical: { label: 'Critical', tone: 'danger' },
}

import { neededByToDisplay } from './opsApiMappers'

const toMin = (value: string) => {
  if (value.includes('T')) {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes()
  }
  const [h, m] = (value || '0:0').split(':').map(Number)
  return h * 60 + m
}

export const OPS_NOW = '07:52'

export const requestSlaState = (r: IOpsRequest, now: string = OPS_NOW): OpsRequestSlaState => {
  if (r.slaState) return r.slaState
  if (r.status === 'done' || r.status === 'declined') return 'on_time'
  const displayTime = neededByToDisplay(r.neededBy)
  const remaining = toMin(displayTime) - toMin(now)
  if (remaining < 0) return 'overdue'
  if (remaining <= 45) return 'due_soon'
  return 'on_time'
}

export const requestSlaLabel = (r: IOpsRequest, now: string = OPS_NOW): string => {
  const state = requestSlaState(r, now)
  const displayTime = neededByToDisplay(r.neededBy)
  const remaining = toMin(displayTime) - toMin(now)
  if (state === 'overdue') return `Overdue ${Math.abs(remaining)}m — needed by ${displayTime}`
  if (state === 'due_soon') return `Due in ${remaining}m — needed by ${displayTime}`
  return `Needed by ${displayTime}`
}

export const SLA_TONE: { [k in OpsRequestSlaState]: IStatusPill['status'] } = {
  'on_time': 'info',
  'due_soon': 'warning',
  overdue: 'danger',
}

export const seedOpsRequests = (): IOpsRequest[] => [
  {
    id: 14,
    code: 'REQ-014',
    fromDept: 'mining',
    toDept: 'maintenance',
    title: 'Tyre change — Truck 12',
    description: 'Truck 12 stopped on Main Haul Road with a tyre pressure fault. Need a fitter with the tyre handler to get it back hauling.',
    linkedEntityLabel: 'Haul cycle #344 — return empty',
    linkedPath: '/operations/task/9104',
    priority: 'critical',
    neededBy: '09:30',
    status: 'in_progress',
    declineReason: null,
    createdBy: 'You (Mining)',
    createdAt: '07:44',
    acknowledgedAt: '07:48',
    escalationLevel: 0,
  },
  {
    id: 15,
    code: 'REQ-015',
    fromDept: 'campServices',
    toDept: 'hr',
    title: 'Roster cover — 1 cleaner short',
    description: 'Camp Block B deep clean is understaffed, 1 of 2 cleaners present. Need cover from the reserve roster for the rest of the shift.',
    linkedEntityLabel: 'Camp Block B deep clean',
    linkedPath: '/operations/d/campServices',
    priority: 'high',
    neededBy: '07:00',
    status: 'open',
    declineReason: null,
    createdBy: 'Thomas P. (Camp Services)',
    createdAt: '06:20',
    acknowledgedAt: null,
    escalationLevel: 1,
  },
  {
    id: 16,
    code: 'REQ-016',
    fromDept: 'plant',
    toDept: 'maintenance',
    title: 'Feed chute inspection assist',
    description: 'Crusher feed down 25% — need a fitter to check the feed chute liner while the mining side swaps trucks.',
    linkedEntityLabel: 'Crusher feed rate check',
    linkedPath: '/operations/task/9113',
    priority: 'high',
    neededBy: '08:30',
    status: 'open',
    declineReason: null,
    createdBy: 'Grace M. (Processing Plant)',
    createdAt: '07:45',
    acknowledgedAt: null,
    escalationLevel: 0,
  },
]
